import restaurantsDataJson from '../assets/best-restaurants/restaurantsData.json';
import whereToEatGuideJson from '../assets/wheretoeatguide.json';
import {GeoFaqItem, GeoRestaurantMock, getGeoCityContent} from './geoContent.mock';
import {GeoCityConfig} from './geoRoutes';

type RestaurantDataRow = {
    name: string;
    cuisineType: string;
    restaurantImage?: string;
    soltDishImage?: string;
    soltDishImageLink?: string;
    priceRange?: string;
    starRating?: number;
    description: string;
};

type RestaurantsDataCity = {
    restaurants?: RestaurantDataRow[];
};

type WhereToEatGuideEntry = {
    city: string;
    subtitle: string;
    image: string;
};

export type GeoHubRestaurant = GeoRestaurantMock & {
    dishImage?: string;
    restaurantImage?: string;
    priceRange?: string;
    starRating?: number;
    /** Long copy for SEO / assistants (from restaurantsData when available). */
    fullDescription: string;
    /** From Mongo when loaded live */
    postsCount?: number;
    formattedAddress?: string;
    cardLink?: {kind: 'place'; href: string} | {kind: 'app'; href: string};
};

export type GeoHubViewModel = {
    bannerSrc?: string;
    subtitle: string;
    restaurants: GeoHubRestaurant[];
    bestFor: string[];
    aboutCity?: string;
    faq: GeoFaqItem[];
};

const CITY_BANNERS: Record<string, string> = {
    'bucharest-banner.jpeg': require('../assets/bucharest-banner.jpeg'),
    'lisbon-banner.jpeg': require('../assets/lisbon-banner.jpeg'),
    'seville-banner.jpeg': require('../assets/seville-banner.jpeg'),
};

function richRestaurantsByPath(restaurantsDataKey: string): Map<string, RestaurantDataRow> {
    const city = (restaurantsDataJson as Record<string, RestaurantsDataCity>)[
        restaurantsDataKey
    ];
    const map = new Map<string, RestaurantDataRow>();
    if (!city?.restaurants) {
        return map;
    }
    for (const row of city.restaurants) {
        if (row.soltDishImageLink) {
            map.set(row.soltDishImageLink, row);
        }
    }
    return map;
}

export function enrichHubRestaurants(
    restaurantsDataKey: string,
    restaurants: GeoRestaurantMock[],
): GeoHubRestaurant[] {
    const byPath = richRestaurantsByPath(restaurantsDataKey);
    return restaurants.map((r) => {
        const rich = r.soltPath ? byPath.get(r.soltPath) : undefined;
        return {
            ...r,
            cuisine: rich?.cuisineType ?? r.cuisine,
            dishImage: rich?.soltDishImage,
            restaurantImage: rich?.restaurantImage,
            priceRange: rich?.priceRange,
            starRating: rich?.starRating,
            fullDescription: rich?.description ?? r.description,
        };
    });
}

export function sortHubRestaurants(
    restaurants: GeoHubRestaurant[],
    featuredPaths?: string[],
): GeoHubRestaurant[] {
    if (!featuredPaths?.length) {
        return restaurants;
    }
    const order = new Map(featuredPaths.map((path, index) => [path, index]));
    return [...restaurants].sort((a, b) => {
        const aIndex = a.soltPath ? order.get(a.soltPath) ?? 999 : 999;
        const bIndex = b.soltPath ? order.get(b.soltPath) ?? 999 : 999;
        return aIndex - bIndex;
    });
}

export function getGeoHubViewModel(geo: GeoCityConfig): GeoHubViewModel | undefined {
    const content = getGeoCityContent(geo.restaurantsDataKey);
    if (!content) {
        return undefined;
    }

    const guideKey = geo.whereToEatGuideKey ?? geo.restaurantsDataKey;
    const guide = (whereToEatGuideJson as WhereToEatGuideEntry[]).find(
        (entry) => entry.city === guideKey,
    );

    const restaurants = sortHubRestaurants(
        enrichHubRestaurants(geo.restaurantsDataKey, content.restaurants),
        geo.featuredRestaurantPaths,
    );

    const bannerSrc = guide?.image ? CITY_BANNERS[guide.image] : undefined;

    return {
        bannerSrc,
        subtitle: guide?.subtitle ?? content.hubIntro,
        restaurants,
        bestFor: content.bestFor,
        aboutCity: content.aboutCity,
        faq: content.faq,
    };
}
